import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  CreditCard, Check, X, ArrowLeft, Loader2, 
  Building2, Calendar, CheckCircle, AlertTriangle 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Subscription() {
  const { user, token, companyContext } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'yearly'
  
  const companyId = companyContext?.company_id || user?.company_id;
  const sessionId = searchParams.get('session_id');
  
  useEffect(() => {
    fetchData();
    
    // Check payment status if returning from Stripe
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [companyId, sessionId]);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const [plansRes, subRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/subscriptions/plans`),
        companyId ? axios.get(`${BACKEND_URL}/api/subscriptions/company/${companyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }) : Promise.resolve({ data: null })
      ]);
      
      setPlans(plansRes.data.plans || []);
      if (subRes.data) {
        setSubscription(subRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your email for confirmation.');
      return;
    }
    
    setCheckingPayment(true);
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/subscriptions/status/${sid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription is now active.');
        fetchData();
        setCheckingPayment(false);
        // Clear the session_id from URL
        navigate('/subscription', { replace: true });
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingPayment(false);
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setCheckingPayment(false);
    }
  };
  
  const handleSubscribe = async (planId) => {
    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${BACKEND_URL}/api/subscriptions/checkout`, {
        plan_id: planId,
        company_id: companyId,
        origin_url: originUrl,
        billing_cycle: billingCycle
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to create checkout session'); toast.error(msg);
    }
  };
  
  const getPrice = (plan) => {
    return billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
  };
  
  const getPriceDisplay = (plan) => {
    if (billingCycle === 'yearly') {
      const monthlyEquiv = (plan.price_yearly / 12).toFixed(2);
      return (
        <div>
          <span className="text-3xl font-bold">£{monthlyEquiv}</span>
          <span className="text-slate-500">/mo</span>
          <div className="text-xs text-green-600 font-medium">
            £{plan.price_yearly}/year (Save £{plan.yearly_savings})
          </div>
        </div>
      );
    }
    return (
      <div>
        <span className="text-3xl font-bold">£{plan.price_monthly}</span>
        <span className="text-slate-500">/mo</span>
      </div>
    );
  };
  
  const isCurrentPlan = (planId) => {
    return subscription?.subscription?.tier === planId;
  };
  
  const getTrialDaysRemaining = () => {
    if (!subscription?.subscription?.trial_ends_at) return 0;
    const trialEnd = new Date(subscription.subscription.trial_ends_at);
    const now = new Date();
    const days = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-slate-600">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-300" />
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-blue-600" />
                <h1 className="text-lg font-semibold text-slate-900">Subscription</h1>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Payment Status Banner */}
        {checkingPayment && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
            <span className="text-blue-800">Checking payment status...</span>
          </div>
        )}
        
        {/* Current Subscription Status */}
        {subscription && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                {subscription.company_name}
              </CardTitle>
              <CardDescription>Current subscription status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-1">Status</p>
                  <Badge className={
                    subscription.status === 'active' ? 'bg-green-100 text-green-800' :
                    subscription.status === 'trial' ? 'bg-blue-100 text-blue-800' :
                    'bg-red-100 text-red-800'
                  }>
                    {subscription.status}
                  </Badge>
                </div>
                
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-1">Plan</p>
                  <p className="font-semibold capitalize">{subscription.subscription?.tier || 'None'}</p>
                </div>
                
                {subscription.subscription?.tier === 'trial' && (
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <p className="text-sm text-amber-700 mb-1">Trial Period</p>
                    <p className="font-semibold text-amber-800">
                      {getTrialDaysRemaining()} days remaining
                    </p>
                  </div>
                )}
                
                {subscription.subscription?.subscription_ends_at && subscription.subscription?.tier !== 'trial' && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 mb-1">Renews</p>
                    <p className="font-semibold">
                      {new Date(subscription.subscription.subscription_ends_at).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
              
              {subscription.trial_expired && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <div>
                    <p className="font-medium text-red-800">Trial Expired</p>
                    <p className="text-sm text-red-600">Please select a plan to continue using the service.</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Pricing Plans */}
        <div className="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
          <h2 className="text-2xl font-bold text-slate-900">Choose Your Plan</h2>
          
          {/* Billing Cycle Toggle */}
          <div className="flex items-center gap-3 bg-slate-100 p-2 rounded-lg">
            <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-slate-900' : 'text-slate-500'}`}>
              Monthly
            </span>
            <Switch
              checked={billingCycle === 'yearly'}
              onCheckedChange={(checked) => setBillingCycle(checked ? 'yearly' : 'monthly')}
              data-testid="billing-cycle-toggle"
            />
            <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-slate-900' : 'text-slate-500'}`}>
              Yearly
            </span>
            {billingCycle === 'yearly' && (
              <Badge className="bg-green-100 text-green-800 text-xs">Save 10-15%</Badge>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card 
              key={plan.id} 
              className={`relative ${plan.id === 'standard' ? 'border-blue-500 border-2' : ''} ${isCurrentPlan(plan.id) ? 'ring-2 ring-green-500' : ''}`}
            >
              {plan.id === 'standard' && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-blue-600 text-white">Most Popular</Badge>
                </div>
              )}
              
              {isCurrentPlan(plan.id) && (
                <div className="absolute -top-3 right-4">
                  <Badge className="bg-green-600 text-white">Current Plan</Badge>
                </div>
              )}
              
              <CardHeader>
                <CardTitle>{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  {getPriceDisplay(plan)}
                </div>
                
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Memorial Management</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Stock Management</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Artwork Tools</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Email Sending</span>
                  </li>
                  <li className="flex items-center gap-2">
                    {plan.features?.white_label_enabled ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <X className="h-4 w-4 text-slate-300" />
                    )}
                    <span className={`text-sm ${!plan.features?.white_label_enabled ? 'text-slate-400' : ''}`}>
                      White-Label Branding
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    {plan.features?.worktops_enabled ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <X className="h-4 w-4 text-slate-300" />
                    )}
                    <span className={`text-sm ${!plan.features?.worktops_enabled ? 'text-slate-400' : ''}`}>
                      Worktop Management
                    </span>
                  </li>
                </ul>
                
                <Button 
                  className={`w-full ${plan.id === 'standard' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                  variant={plan.id === 'standard' ? 'default' : 'outline'}
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={isCurrentPlan(plan.id) && subscription?.status === 'active'}
                >
                  {isCurrentPlan(plan.id) && subscription?.status === 'active' ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Current Plan
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4 mr-2" />
                      Subscribe
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
        
        {/* Payment History */}
        {subscription?.payment_history?.length > 0 && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Payment History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-slate-100">
                {subscription.payment_history.map((tx) => (
                  <div key={tx.id} className="py-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium">{tx.plan_id} Plan</p>
                      <p className="text-sm text-slate-500">
                        {new Date(tx.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">£{tx.amount}</p>
                      <Badge className="bg-green-100 text-green-800">{tx.payment_status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
